// auth — client-side helpers for the unified SENTRY Accounts login.
//
// Unified login (SEN-634): the self-built email/password + Google / Apple /
// LINE dashboard sign-in has been retired. NEXUS is now purely a relying party
// of the central IdP (accounts.sentry.red) — the only login entry point is
// `accountsInitUrl()`. What remains here is session inspection (/v1/auth/me),
// the Accounts init URL, and friendly error mapping for callback failures.
//
// Endpoints still consumed:
//   GET  /v1/auth/me
//   GET  /v1/auth/accounts/init           (top-level redirect → Accounts OIDC)
//
// Session is carried via httpOnly cookies (nexus_session / nexus_refresh) set
// on `.nexus.sentry.red` by the API. The SPA cannot read those cookies;
// presence is inferred via a separate non-httpOnly hint cookie
// `nexus_session_present=1` OR by a successful `/v1/auth/me` round trip. We
// never bake JWTs into JS state.

import { apiFetch, resolveApiBase } from "./api-client.js";

// ── Types ─────────────────────────────────────────────────────────────

export interface MeResponse {
  ok: true;
  user_id: string;
  email: string;
  display_name: string | null;
  avatar_url?: string | null;
  role: string;
  scopes: string[];
  /** Permission-based authorization (Custom RBAC §4). Computed server-side
   *  from user_role_assignments union (or legacy role fallback). Used by
   *  dashboard route gating / sidebar filter going forward. */
  permissions?: string[];
  /** Canonical scope wildcard string (eg 'admin:platform admin:tenant'). */
  scope?: string;
  super_admin: boolean;
  /** '_platform' for super_admin; tenant_id for tenant member; null when
   *  authenticated but unbound (membership_required=true). */
  tenant_id: string | null;
  /** Tenant the super_admin chose to act as. */
  acting_tenant_id?: string | null;
  /** Human-readable tenant name for the chip — set when acting_tenant_id is set. */
  acting_tenant_name?: string | null;
  /** True when the user authenticated but has no tenant_membership yet. */
  membership_required?: boolean;
  /** ISO-8601 session expiry. Absent on the membership_required branch. */
  expires_at?: string;
}

export interface AuthError {
  error: string;
  reason?: string;
  correlation_id?: string;
}

// ── Public helpers ────────────────────────────────────────────────────

/** Returns the current session via /v1/auth/me, or null when not signed in. */
export async function fetchMe(): Promise<MeResponse | null> {
  try {
    const r = await apiFetch("/v1/auth/me", { credentials: "include" });
    if (r.status === 401 || r.status === 403) return null;
    if (!r.ok) return null;
    const body = (await r.json()) as MeResponse | AuthError;
    if ("error" in body) return null;
    return body;
  } catch {
    return null;
  }
}

/** Quick non-blocking check used by Landing's "主控台" button. */
export function hasSessionHint(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split("; ").some((c) => c.startsWith("nexus_session_present="));
}

// ── Accounts OIDC init ────────────────────────────────────────────────

/**
 * SENTRY Accounts (accounts.sentry.red) OIDC login — NEXUS is a relying party
 * of the central IdP. Same top-level-redirect semantics as the other
 * providers; the server runs code+PKCE, verifies the id_token offline (ES256),
 * and returns a NEXUS session. Needs no product secret provisioned, so this
 * entry point is always live.
 */
export function accountsInitUrl(returnTo: string): string {
  return `${resolveApiBase()}/v1/auth/accounts/init?return_to=${encodeURIComponent(returnTo)}`;
}

// ── Friendly error mapping ────────────────────────────────────────────

// Only the SENTRY Accounts callback error codes remain reachable now that the
// self-built email/password + Google / Apple / LINE flows are retired.
const ERROR_MESSAGES_ZH: Record<string, string> = {
  jwt_signing_not_configured: "系統尚未完成設定，請稍後再試。",
  auth_not_configured: "登入尚未設定完成，請稍後再試。",
  accounts_token_exchange_failed: "SENTRY 帳號登入過程失敗（token 交換），請重試。",
  accounts_id_token_invalid: "SENTRY 帳號身分驗證失敗，請重試。",
  accounts_email_unverified: "此 SENTRY 帳號的 Email 尚未驗證，請先完成驗證再登入。",
  not_authorized: "此帳號未被授權使用 NEXUS。",
  oauth_state_invalid: "登入狀態已逾時，請重試。",
  sso_sub_conflict: "此帳號已綁定其他使用者，請聯絡管理員。",
  user_disabled: "此帳號已停用，請聯絡管理員。",
  session_issue_failed: "無法建立登入工作階段，請稍後再試。",
  invalid_callback: "登入回呼資料不完整，請重試。",
  rate_limited: "嘗試次數過多，請稍後再試。",
};

export function friendlyAuthError(err: unknown): string {
  if (!err) return "發生未知錯誤，請稍後再試。";
  const e = err as Partial<AuthError> & { message?: string };
  if (e?.error && ERROR_MESSAGES_ZH[e.error]) return ERROR_MESSAGES_ZH[e.error];
  if (e?.message) return e.message;
  return "發生未知錯誤，請稍後再試。";
}
